import { Component } from 'react';
import changelogEntries from '../../data/changelog';
import renderInline from './renderInline';
import Styled from './Changelog.style';

// The entry's headline date is the STAGING date — always a confirmed ISO date, it
// is the entry's title, the file name (<staging-date>.json), and the sort key.
const headlineDate = (entry) => entry.staging;

// Sort newest-first by headline (staging) date. ISO dates sort lexicographically.
const sortByHeadline = (entries) => [...entries].sort((a, b) => {
    const da = headlineDate(a);
    const db = headlineDate(b);

    return da < db ? 1 : da > db ? -1 : 0;
});

// Entries are auto-discovered from src/data/changelog/*.json in arbitrary order.
const CHANGELOG = sortByHeadline(changelogEntries);

// Keep-a-Changelog categories, rendered in this canonical order when present.
const CATEGORY_ORDER = ['added', 'changed', 'deprecated', 'removed', 'fixed', 'security'];

const CATEGORY_LABELS = {
    added: 'Added',
    changed: 'Changed',
    deprecated: 'Deprecated',
    removed: 'Removed',
    fixed: 'Fixed',
    security: 'Security'
};

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

// Format 'YYYY-MM-DD' as 'D Month YYYY' without timezone surprises (no Date parsing).
const formatDate = (iso) => {
    const [year, month, day] = String(iso).split('-').map(Number);

    if (!year || !month || !day) {
        return iso;
    }

    return `${day} ${MONTHS[month - 1]} ${year}`;
};

// Resolve the `production` field into { text, pending } for display.
//   'YYYY-MM-DD'             -> live on that date
//   { expected: 'YYYY-MM-DD' } -> "Expected 25 Jun 2026" (pending)
//   'expected' / null / undefined -> "To be expected" (pending)
const formatProduction = (production) => {
    if (typeof production === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(production)) {
        return { text: formatDate(production), pending: false };
    }

    if (production && typeof production === 'object' && production.expected) {
        return { text: `Expected ${formatDate(production.expected)}`, pending: true };
    }

    return { text: 'To be expected', pending: true };
};

const renderItem = (item, key) => {
    // Normal content: a plain line — NOT a bullet.
    if (typeof item === 'string') {
        return <p key={key} className="changelog-line">{renderInline(item)}</p>;
    }

    if (item && item.diff) {
        const { field, from, to } = item.diff;

        return (
            <div key={key} className="changelog-diff">
                <span className="field">{field}:</span>
                <span className="from">{from}</span>
                <span className="arrow">→</span>
                <span className="to">{to}</span>
            </div>
        );
    }

    if (item && item.table) {
        const { headers = [], rows = [] } = item.table;

        return (
            <table key={key} className="changelog-table">
                {headers.length > 0 && (
                    <thead>
                        <tr>
                            {headers.map((header, i) => <th key={i}>{renderInline(header)}</th>)}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {rows.map((row, r) => (
                        <tr key={r}>
                            {row.map((cell, c) => <td key={c}>{renderInline(cell)}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    // Opt-in bullet list: explicit points, indented under the normal content.
    if (item && item.list) {
        return (
            <ul key={key} className="changelog-list">
                {item.list.map((point, i) => <li key={i}>{renderInline(point)}</li>)}
            </ul>
        );
    }

    // Labeled sub-section: a small heading (e.g. "Request") above its own bullets.
    if (item && item.group) {
        const { label, items = [] } = item.group;

        return (
            <div key={key} className="changelog-group">
                <span className="changelog-group-label">{renderInline(label)}</span>
                <ul className="changelog-list">
                    {items.map((point, i) => <li key={i}>{renderInline(point)}</li>)}
                </ul>
            </div>
        );
    }

    return null;
};

// True when a change-set (`changes` or `breaking`) has at least one item.
const hasChanges = (changeSet) => (
    !!changeSet && CATEGORY_ORDER.some((category) => (
        changeSet[category] && changeSet[category].length > 0
    ))
);

// Render the Keep-a-Changelog categories of one change-set, in canonical order.
const renderCategories = (changeSet) => (
    CATEGORY_ORDER.filter((category) => (
        changeSet[category] && changeSet[category].length > 0
    )).map((category) => (
        <div className="changelog-category" key={category}>
            <span className={`changelog-badge ${category}`}>
                {CATEGORY_LABELS[category]}
            </span>
            <div className="changelog-body">
                {changeSet[category].map((item, i) => renderItem(item, i))}
            </div>
        </div>
    ))
);

// One update within a release: optional title/description/action plus its
// breaking and/or non-breaking change sections.
const ChangelogUpdate = ({ update }) => {
    const hasBreaking = hasChanges(update.breaking);
    const hasNormal = hasChanges(update.changes);

    return (
        <div className="changelog-update">
            {update.title && (
                <h3 className="changelog-entry-subtitle">{update.title}</h3>
            )}
            {update.description && (
                <p className="changelog-entry-description">{renderInline(update.description)}</p>
            )}

            {update.update && update.update.length > 0 && (
                <div className="changelog-section changelog-section--update">
                    <h3 className="changelog-section-title">Update</h3>
                    <div className="changelog-body">
                        {update.update.map((item, i) => renderItem(item, i))}
                    </div>
                </div>
            )}

            {update.action && update.action.length > 0 && (
                <div className="changelog-section changelog-section--action">
                    <h3 className="changelog-section-title">What this means for you</h3>
                    <div className="changelog-body">
                        {update.action.map((item, i) => renderItem(item, i))}
                    </div>
                </div>
            )}

            {hasBreaking && (
                <div className="changelog-section changelog-section--breaking">
                    <h3 className="changelog-section-title">⚠ Breaking changes</h3>
                    {renderCategories(update.breaking)}
                </div>
            )}

            {hasNormal && (
                <div className="changelog-section">
                    {renderCategories(update.changes)}
                </div>
            )}
        </div>
    );
};

// One changelog entry. Extracted so an error boundary can isolate a single
// malformed entry instead of letting it blank the entire list.
const ChangelogEntry = ({ entry }) => {
    const production = formatProduction(entry.production);
    const updates = entry.updates || [];
    const hasBreaking = updates.some((update) => hasChanges(update.breaking));

    return (
        <section className="changelog-entry">
            <h2 className="changelog-entry-title">
                {formatDate(headlineDate(entry))}
                {hasBreaking && <span className="changelog-breaking-badge">⚠ Breaking Changes</span>}
            </h2>
            <div className="changelog-envs">
                <span className="changelog-env">
                    <span className="env-label staging">Staging</span>
                    <span className="env-date">{formatDate(entry.staging)}</span>
                </span>
                <span className="changelog-env">
                    <span className="env-label production">Production</span>
                    <span className={`env-date${production.pending ? ' env-pending' : ''}`}>
                        {production.text}
                    </span>
                </span>
            </div>

            {updates.map((update, i) => <ChangelogUpdate key={i} update={update} />)}
        </section>
    );
};

// Per-entry error boundary: a single malformed entry is dropped from the page
// (and logged loudly) instead of crashing the whole changelog render.
class EntryBoundary extends Component {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error) {
        console.error(`Changelog: failed to render entry "${this.props.label}"`, error);
    }

    render() {
        // A malformed entry is dropped from the page (so it can't blank the whole
        // list) and reported via console.error in componentDidCatch — we don't
        // surface internal error text to public changelog readers.
        if (this.state.failed) {
            return null;
        }

        return this.props.children;
    }
}

class Changelog extends Component {
    render() {
        // Defaults to the auto-discovered entries; the playground passes sample data.
        const entries = this.props.entries ? sortByHeadline(this.props.entries) : CHANGELOG;
        const title = this.props.title || 'Changelog';

        return (
            <Styled>
                <h1 className="changelog-title">{title}</h1>

                {entries.length === 0 && (
                    <p className="changelog-empty">No changes have been published yet.</p>
                )}

                {(() => {
                    // The staging date is the file name, so it must be unique per
                    // entry. If two entries collide on it (filename convention broken),
                    // fail loud — but still render every entry.
                    const seen = new Set();

                    return entries.map((entry, index) => {
                        const headline = headlineDate(entry);

                        if (seen.has(headline)) {
                            console.error(`Changelog: duplicate staging date "${headline}" — each entry must have a unique staging date (it is the file name). Fix the colliding entry.`);
                        }
                        seen.add(headline);

                        return (
                            <EntryBoundary key={`${headline}#${index}`} label={headline}>
                                <ChangelogEntry entry={entry} />
                            </EntryBoundary>
                        );
                    });
                })()}
            </Styled>
        );
    }
}

export default Changelog;
